import { coreApi, mercuryApi } from '$api'
import { Country, PayrollSystem } from '$constants'
import {
  StaffMember,
  IStaffMemberDetail,
  transformStaffMemberOnGet,
  transformStaffMemberOnSave,
  transformStaffMemberOnUpdate,
  IPosStaffMember,
  IAvatarFile,
  FileParameter,
} from '$models'
import { getActiveCompanySettings, singletonQueryClient } from '$utils'
import { Dayjs } from 'dayjs'

enum QueryKeys {
  GetAll = 'all-staff-members',
  GetPosStaffMembers = 'pos-staff-members',
}

/**
 * Get all Staff Members
 * @param activeAsOfDate - if set, only include staff members active at this time
 * @param includeDeleted - include the ones with datestampDeleted = true
 * @param includeLastClocks - include last clock data for each staff member
 * @param from - optional explicit start date for remuneration timeline filtering (YYYY-MM-DD)
 * @param to - optional explicit end date for remuneration timeline filtering (YYYY-MM-DD)
 * @returns Promise<IStaffMemberDetail[]>
 */
export const getAll = (
  activeAsOfDate?: Dayjs,
  includeDeleted: boolean = true,
  includeLastClocks: boolean = true,
  from?: string,
  to?: string
): Promise<IStaffMemberDetail[]> => {
  return singletonQueryClient.fetchQuery({
    queryKey: [
      QueryKeys.GetAll,
      activeAsOfDate,
      includeDeleted,
      includeLastClocks,
      from,
      to,
    ],
    queryFn: async ({ signal }) => {
      return await coreApi.getStaffMembers(
        activeAsOfDate,
        from,
        to,
        includeDeleted,
        includeLastClocks,
        signal
      )
    },
  })
}

/**
 * Get Staff Member by id
 * @param staffMemberId - staff member id to get
 * @returns Promise<StaffMember>
 */
export const getById = async (staffMemberId: string): Promise<StaffMember> => {
  const { payrollSystemPreference, countryPreference } =
    getActiveCompanySettings()

  const result = await mercuryApi.get<StaffMember>(
    `/staff-members/${staffMemberId}`
  )
  const updatedStaffMember = transformStaffMemberOnGet(
    result,
    payrollSystemPreference,
    countryPreference
  )

  return updatedStaffMember
}

/**
 * Get Staff Member by id
 * @param staffMemberId - staff member id to get
 * @param country - company country received through query string params
 * @param payrollSystem - company payroll system received through query string params
 * @returns Promise<T>
 */
export const getPersonalDetailsById = async (
  staffMemberId: string,
  country?: Country,
  payrollSystem?: PayrollSystem
): Promise<StaffMember> => {
  const result = await mercuryApi.get<StaffMember>(
    `/staff-members/personal-details/${staffMemberId}`
  )

  const updatedStaffMember = transformStaffMemberOnGet(
    result,
    payrollSystem,
    country
  )
  return updatedStaffMember
}

/**
 * Update staff member
 * @param staffMember - staff member to update
 * @returns Promise<T>
 */
export const update = async (
  staffMember: StaffMember
): Promise<StaffMember> => {
  const { payrollSystemPreference, countryPreference } =
    getActiveCompanySettings()

  const transformedStaffMember: object = transformStaffMemberOnUpdate(
    payrollSystemPreference,
    staffMember,
    countryPreference
  )
  const updatedStaffMember = await mercuryApi.update(
    `/staff-members/${staffMember.id}`,
    transformedStaffMember
  )

  const transformedStaffMemberWhenGet = transformStaffMemberOnGet(
    updatedStaffMember,
    payrollSystemPreference,
    countryPreference
  )

  return transformedStaffMemberWhenGet
}

/**
 * Update staff member
 * @param staffMember - staff member to update
 * @param country - company country received through query string params
 * @param payrollSystem - company payroll system received through query string params
 * @returns Promise<T>
 */
export const updatePersonalDetails = async (
  staffMember: StaffMember,
  country: Country,
  payrollSystem: PayrollSystem
): Promise<StaffMember> => {
  const transformedStaffMember: object = transformStaffMemberOnUpdate(
    payrollSystem,
    staffMember,
    country
  )
  const updatedStaffMember = await mercuryApi.update(
    `/staff-members/personal-details/${staffMember.id}`,
    transformedStaffMember
  )

  const transformedStaffMemberWhenGet = transformStaffMemberOnGet(
    updatedStaffMember,
    payrollSystem,
    country
  )

  return transformedStaffMemberWhenGet
}

/**
 * Add staff member
 * @param staffMember - staff member to add
 * @returns Promise<T>
 */
export const add = async (staffMember: StaffMember): Promise<StaffMember> => {
  const { payrollSystemPreference, countryPreference } =
    getActiveCompanySettings()

  const transformedStaffMember: object = transformStaffMemberOnSave(
    payrollSystemPreference,
    staffMember,
    countryPreference
  )

  const addedStaffMember = await mercuryApi.add(
    `/staff-members`,
    transformedStaffMember
  )

  const transformedStaffMemberWhenGet = transformStaffMemberOnGet(
    addedStaffMember,
    payrollSystemPreference,
    countryPreference
  )

  return transformedStaffMemberWhenGet
}

/**
 * Update staff members emails
 * @param updatedEmails - list of tuples of [staffMemberId, email]
 * @returns Promise<boolean>
 */
export const updateStaffMemberEmails = async (
  staffMemberEmails: [string, string][]
): Promise<boolean> => {
  const convertedStaffMemberEmails = staffMemberEmails.map(([id, email]) => ({
    id: id,
    email: email,
  }))

  return await mercuryApi.update('/staff-members/update-emails', {
    updatedEmails: convertedStaffMemberEmails,
  })
}

export const getPosStaffMembers = async (
  includeDeleted: boolean
): Promise<IPosStaffMember[]> => {
  return singletonQueryClient.fetchQuery({
    queryKey: [QueryKeys.GetPosStaffMembers, includeDeleted],
    queryFn: async ({ signal }) => {
      return await coreApi.getPosStaffMembers(includeDeleted, signal)
    },
  })
}

export const getAllStaffMemberAvatars = async (): Promise<
  Record<string, IAvatarFile['avatar']>
> => {
  const { hasTimePermission } = getActiveCompanySettings()

  if (!hasTimePermission) return {}

  // We don't want this to be wrapped in a singletonQueryClient.fetchQuery
  // because the cache is only handled by the service worker, and we don't
  // want to cache through TanStack Query
  const avatars = await coreApi.getAllAvatars()

  return avatars
}

/**
 * Uploads an avatar for a staff member
 * @param rawFile The avatar to be uploaded.
 * @param staffMemberId The staff member ID.
 * @returns A promise resolving to an object containing the uploaded file's avatar data.
 */
export const updateStaffMemberAvatar = async (
  rawFile: File | null,
  staffMemberId: string
): Promise<IAvatarFile> => {
  if (!rawFile) {
    throw new Error('File is required for avatar upload')
  }

  const fileParameter: FileParameter = {
    data: rawFile,
    fileName: `staff-avatar-${staffMemberId}`,
  }

  const response: IAvatarFile = await coreApi.updateStaffMemberAvatarFile(
    staffMemberId,
    fileParameter
  )

  return response
}

/**
 * Deletes an avatar for a staff member
 * @param staffMemberId The staff member ID.
 * @returns A promise that resolves when the avatar is deleted.
 */
export const deleteStaffMemberAvatar = async (
  staffMemberId: string
): Promise<void> => {
  await coreApi.deleteStaffMemberAvatarFile(staffMemberId)
}
